import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseAvailable } from '../../lib/supabase';

const FarmlandLeasingGuide = () => {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPageData();
    incrementViewCount();
  }, []);

  const loadPageData = async () => {
    try {
      // Check if supabase is available and properly configured
      if (!supabase || !isSupabaseAvailable) {
        console.warn('⚠️ Supabase not configured - using offline mode with fallback data');
        setLoading(false);
        return;
      }

      // Improved query structure with better error handling
      const queryBuilder = supabase?.from('seo_pages');
      if (!queryBuilder) {
        console.warn('Failed to create query builder');
        setLoading(false);
        return;
      }

      const selectQuery = queryBuilder?.select('*');
      if (!selectQuery || typeof selectQuery?.eq !== 'function') {
        console.warn('Query builder missing .eq method - using offline mode');
        setLoading(false);
        return;
      }

      const { data, error } = await selectQuery?.eq('slug', 'farmland-leasing-contracts-legal-guide')?.single();

      if (error) {
        console.error('Supabase query error:', error);
        setLoading(false);
        return;
      }

      if (data) {
        setPageData(data);
      }
    } catch (error) {
      console.error('Error loading page data:', error);
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async () => {
    try {
      // Check if supabase is available and properly configured
      if (!supabase || !isSupabaseAvailable) {
        console.warn('⚠️ Supabase not configured - skipping view count increment');
        return;
      }

      // Try RPC function first if available
      if (supabase?.rpc && typeof supabase?.rpc === 'function') {
        const { error: rpcError } = await supabase?.rpc('increment_view_count', { 
          table_slug: 'farmland-leasing-contracts-legal-guide'
        });
        
        if (!rpcError) {
          return; // Successfully incremented via RPC
        }
        console.log('RPC function not available, trying direct update method');
      }

      // Alternative approach using direct update if RPC function doesn't exist
      const updateQuery = supabase?.from('seo_pages');
      if (updateQuery && typeof updateQuery?.update === 'function') {
        const updateBuilder = updateQuery?.update({ 
          view_count: supabase?.raw ? supabase?.raw('view_count + 1') : 1
        });
        
        if (updateBuilder && typeof updateBuilder?.eq === 'function') {
          const { error: updateError } = await updateBuilder?.eq('slug', 'farmland-leasing-contracts-legal-guide');
          
          if (updateError) {
            console.error('Error with direct update:', updateError);
          }
        }
      }
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header calculationData={null} onExportPDF={() => {}} />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 py-12">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded"></div>
                <div className="h-4 bg-muted rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageData?.title || 'Farmland Leasing Contracts Legal Guide | AgRentIndex.com'}</title>
        <meta name="description" content={pageData?.meta_description || 'Essential legal guide for farmland leasing contracts. Learn about lease terms, responsibilities, and legal requirements for agricultural land rental agreements.'} />
        <meta name="keywords" content={pageData?.keywords?.join(', ') || 'farmland lease contracts, agricultural lease agreements, farm rental law'} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageData?.title} />
        <meta property="og:description" content={pageData?.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://agrentindex.com/farmland-leasing-contracts-legal-guide" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData?.title} />
        <meta name="twitter:description" content={pageData?.meta_description} />
        
        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": pageData?.title,
            "description": pageData?.meta_description,
            "author": {
              "@type": "Organization",
              "name": "AgRentIndex"
            },
            "publisher": {
              "@type": "Organization",
              "name": "AgRentIndex",
              "logo": {
                "@type": "ImageObject",
                "url": "https://agrentindex.com/logo.png"
              }
            },
            "datePublished": pageData?.created_at,
            "dateModified": pageData?.updated_at
          })}
        </script>
      </Helmet>
      <Header calculationData={null} onExportPDF={() => {}} />
      <main className="pt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-primary/5 to-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-4">
                <Icon name="Scale" size={16} />
                <span>Legal Guide</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                {pageData?.title || 'Farmland Leasing Contracts Legal Guide'}
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                {pageData?.meta_description}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  onClick={() => navigate('/')}
                  size="lg"
                  iconName="Calculator"
                  iconPosition="left"
                >
                  Calculate Fair Rent
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/agricultural-land-rental-rates-2025')}
                  size="lg"
                  iconName="TrendingUp"
                  iconPosition="left"
                >
                  Market Rates
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mx-4 sm:mx-6 lg:mx-8 max-w-4xl mx-auto -mt-8 relative z-10">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Legal Disclaimer</h3>
              <p className="text-sm text-amber-700 mt-1">
                This guide provides general information only. Always consult with qualified legal counsel 
                for advice specific to your situation and jurisdiction.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Key Points Summary */}
          <div className="bg-card border border-border rounded-xl p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Icon name="Key" size={20} className="mr-2 text-primary" />
              Key Legal Considerations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="Check" size={12} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Property Rights & Boundaries</h3>
                  <p className="text-xs text-muted-foreground">Clear legal descriptions and survey boundaries</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="Check" size={12} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Lease Terms & Renewal</h3>
                  <p className="text-xs text-muted-foreground">Duration, renewal options, and notice requirements</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="Check" size={12} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Liability & Insurance</h3>
                  <p className="text-xs text-muted-foreground">Risk allocation and coverage requirements</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="Check" size={12} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Conservation Compliance</h3>
                  <p className="text-xs text-muted-foreground">Environmental regulations and program requirements</p>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-muted rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="List" size={20} className="mr-2" />
              Table of Contents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <ul className="space-y-1">
                <li><a href="#legal-foundation" className="text-primary hover:underline">Legal Foundation</a></li>
                <li><a href="#essential-elements" className="text-primary hover:underline">Essential Contract Elements</a></li>
                <li><a href="#landlord-rights" className="text-primary hover:underline">Landlord Rights & Responsibilities</a></li>
                <li><a href="#tenant-obligations" className="text-primary hover:underline">Tenant Rights & Obligations</a></li>
              </ul>
              <ul className="space-y-1">
                <li><a href="#specialized-provisions" className="text-primary hover:underline">Specialized Provisions</a></li>
                <li><a href="#dispute-resolution" className="text-primary hover:underline">Dispute Resolution</a></li>
                <li><a href="#modern-considerations" className="text-primary hover:underline">Modern Legal Considerations</a></li>
                <li><a href="#best-practices" className="text-primary hover:underline">Best Practices</a></li>
              </ul>
            </div>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none space-y-8">
            <section id="legal-foundation">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <Icon name="Scale" size={24} className="mr-2 text-primary" />
                Legal Foundation of Agricultural Leases
              </h2>
              <p className="text-muted-foreground mb-4">
                Agricultural leases are governed by state law, which varies significantly across different jurisdictions. 
                Unlike residential or commercial leases, farmland agreements often involve unique considerations such as 
                seasonal access, crop rotation requirements, and conservation compliance.
              </p>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold mb-3">State Law Variations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <h4 className="font-medium text-primary mb-2">Midwestern States</h4>
                    <p className="text-muted-foreground">Strong tenant protections, standardized lease forms</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-primary mb-2">Southern States</h4>
                    <p className="text-muted-foreground">Emphasis on landowner rights, crop-specific regulations</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-primary mb-2">Western States</h4>
                    <p className="text-muted-foreground">Water rights considerations, environmental restrictions</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="essential-elements">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <Icon name="FileText" size={24} className="mr-2 text-primary" />
                Essential Contract Elements
              </h2>
              
              <div className="space-y-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <Icon name="Map" size={16} className="mr-2 text-primary" />
                    Property Description
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Precise legal descriptions are crucial, including survey information, aerial boundaries, 
                    and any exclusions such as building sites or conservation areas.
                  </p>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• GPS coordinates and mapping technology for enhanced accuracy</li>
                    <li>• Aerial photography or satellite imagery references</li>
                    <li>• Clear identification of excluded areas</li>
                    <li>• Access routes and easement descriptions</li>
                  </ul>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <Icon name="Calendar" size={16} className="mr-2 text-primary" />
                    Lease Term and Renewal
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Clearly defined start and end dates, along with renewal options and notice requirements. 
                    Many states have specific statutes regarding agricultural lease terms.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="font-medium mb-2">Typical Terms</h4>
                      <ul className="text-muted-foreground space-y-1">
                        <li>• Annual leases (March 1 - February 28)</li>
                        <li>• Multi-year agreements (3-5 years)</li>
                        <li>• Crop-season specific terms</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Renewal Provisions</h4>
                      <ul className="text-muted-foreground space-y-1">
                        <li>• Automatic renewal clauses</li>
                        <li>• Notice requirements (typically 6 months)</li>
                        <li>• Rent adjustment mechanisms</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <div className="bg-primary/5 rounded-xl p-8 text-center my-12">
              <h2 className="text-2xl font-bold mb-4">Need Fair Rent Analysis?</h2>
              <p className="text-muted-foreground mb-6">
                Get data-driven rental rate calculations to support your lease negotiations and ensure fair agreements for both parties.
              </p>
              <Button
                onClick={() => navigate('/')}
                size="lg"
                iconName="Calculator"
                iconPosition="left"
              >
                Calculate Fair Rent Now
              </Button>
            </div>

            <section id="modern-considerations">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <Icon name="Smartphone" size={24} className="mr-2 text-primary" />
                Modern Legal Considerations
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <Icon name="Database" size={16} className="mr-2 text-primary" />
                    Technology & Data Ownership
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Modern farming generates valuable data through precision agriculture systems. 
                    Lease agreements should address data ownership, sharing rights, and privacy concerns.
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Yield mapping and soil analysis data</li>
                    <li>• Equipment telemetry and usage data</li>
                    <li>• Satellite imagery and drone surveys</li>
                  </ul>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <Icon name="Leaf" size={16} className="mr-2 text-primary" />
                    Environmental Regulations
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Contemporary leases must address evolving environmental regulations, 
                    including water quality standards and climate change adaptation requirements.
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Water quality protection measures</li>
                    <li>• Carbon credit opportunities</li>
                    <li>• Sustainable farming practice requirements</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>

          {/* Professional Resources */}
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-8 mt-12">
            <h2 className="text-xl font-semibold mb-4">Professional Legal Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="Users" size={24} className="text-primary" />
                </div>
                <h3 className="font-medium mb-2">State Farm Bureaus</h3>
                <p className="text-sm text-muted-foreground">
                  Standardized lease forms and local legal resources
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="BookOpen" size={24} className="text-primary" />
                </div>
                <h3 className="font-medium mb-2">Extension Services</h3>
                <p className="text-sm text-muted-foreground">
                  Educational programs and lease negotiation guidance
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="Scale" size={24} className="text-primary" />
                </div>
                <h3 className="font-medium mb-2">Agricultural Attorneys</h3>
                <p className="text-sm text-muted-foreground">
                  Specialized legal counsel for complex agreements
                </p>
              </div>
            </div>
          </div>
        </article>
      </main>
      {/* Footer */}
      <footer className="bg-muted mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Icon name="Wheat" size={20} className="text-primary" />
              <span className="text-lg font-semibold">AgRentIndex.com</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} AgRentIndex Agricultural Analytics. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FarmlandLeasingGuide;